import React, { useEffect, useRef, useState } from 'react';
import { Form, Input, Message, Modal } from 'semantic-ui-react';

import Button from '../../../components/Button';
import { AddressService } from '../../../services/address';
import { maskZipCode } from '../../../services/masks';
import { walletPropertyTypeOptions } from '../constants';

export type CreatePropertyFormValues = {
  identification: string;
  propertyType: string;
  area: string;
  zipcode: string;
  street: string;
  neighborhood: string;
  number: string;
  complement: string;
  city: string;
  state: string;
};

type Props = {
  open: boolean;
  isSubmitting: boolean;
  userId?: string;
  isLadoBom: boolean;
  submitError: string | null;
  onClose: () => void;
  onSubmit: (values: CreatePropertyFormValues) => Promise<void>;
};

const initialValues: CreatePropertyFormValues = {
  identification: '',
  propertyType: '',
  area: '',
  zipcode: '',
  street: '',
  neighborhood: '',
  number: '',
  complement: '',
  city: '',
  state: '',
};

const ufOptions = [
  { key: 'AC', text: 'Acre', value: 'AC' },
  { key: 'AL', text: 'Alagoas', value: 'AL' },
  { key: 'AP', text: 'Amapá', value: 'AP' },
  { key: 'AM', text: 'Amazonas', value: 'AM' },
  { key: 'BA', text: 'Bahia', value: 'BA' },
  { key: 'CE', text: 'Ceará', value: 'CE' },
  { key: 'DF', text: 'Distrito Federal', value: 'DF' },
  { key: 'ES', text: 'Espírito Santo', value: 'ES' },
  { key: 'GO', text: 'Goiás', value: 'GO' },
  { key: 'MA', text: 'Maranhão', value: 'MA' },
  { key: 'MT', text: 'Mato Grosso', value: 'MT' },
  { key: 'MS', text: 'Mato Grosso do Sul', value: 'MS' },
  { key: 'MG', text: 'Minas Gerais', value: 'MG' },
  { key: 'PA', text: 'Pará', value: 'PA' },
  { key: 'PB', text: 'Paraíba', value: 'PB' },
  { key: 'PR', text: 'Paraná', value: 'PR' },
  { key: 'PE', text: 'Pernambuco', value: 'PE' },
  { key: 'PI', text: 'Piauí', value: 'PI' },
  { key: 'RJ', text: 'Rio de Janeiro', value: 'RJ' },
  { key: 'RN', text: 'Rio Grande do Norte', value: 'RN' },
  { key: 'RS', text: 'Rio Grande do Sul', value: 'RS' },
  { key: 'RO', text: 'Rondônia', value: 'RO' },
  { key: 'RR', text: 'Roraima', value: 'RR' },
  { key: 'SC', text: 'Santa Catarina', value: 'SC' },
  { key: 'SP', text: 'São Paulo', value: 'SP' },
  { key: 'SE', text: 'Sergipe', value: 'SE' },
  { key: 'TO', text: 'Tocantins', value: 'TO' },
];

const CreatePropertyModal: React.FC<Props> = ({
  open,
  isSubmitting,
  userId,
  isLadoBom,
  submitError,
  onClose,
  onSubmit,
}) => {
  const [values, setValues] = useState<CreatePropertyFormValues>(initialValues);
  const [errors, setErrors] = useState<Partial<Record<keyof CreatePropertyFormValues, string>>>(
    {},
  );
  const [isZipLoading, setIsZipLoading] = useState(false);
  const [zipLookupError, setZipLookupError] = useState<string | null>(null);
  const lastFetchedZipRef = useRef('');

  useEffect(() => {
    if (!open) return;
    setValues(initialValues);
    setErrors({});
    setZipLookupError(null);
    lastFetchedZipRef.current = '';
  }, [open]);

  const validate = (
    currentValues: CreatePropertyFormValues,
  ): Partial<Record<keyof CreatePropertyFormValues, string>> => {
    const validationErrors: Partial<Record<keyof CreatePropertyFormValues, string>> = {};
    const requiredFields: Array<keyof CreatePropertyFormValues> = [
      'propertyType',
      'zipcode',
      'street',
      'neighborhood',
      'number',
      'city',
      'state',
    ];

    if (!isLadoBom) {
      requiredFields.push('identification', 'area');
    }

    requiredFields.forEach((field) => {
      if (!String(currentValues[field] || '').trim()) {
        validationErrors[field] = 'Campo obrigatório.';
      }
    });

    const area = Number(String(currentValues.area || '').replace(',', '.'));
    if (String(currentValues.area || '').trim() && Number.isNaN(area)) {
      validationErrors.area = 'Informe um valor numérico válido.';
    } else if (!Number.isNaN(area) && area < 0) {
      validationErrors.area = 'A área não pode ser negativa.';
    }

    return validationErrors;
  };

  const updateField = (field: keyof CreatePropertyFormValues, value: string) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
    if (field === 'zipcode') {
      setZipLookupError(null);
    }
  };

  const lookupZipCode = async (zipcode: string) => {
    const digits = zipcode.replace(/\D/g, '');
    if (digits.length !== 8) return;
    if (lastFetchedZipRef.current === digits) return;

    lastFetchedZipRef.current = digits;

    try {
      setZipLookupError(null);
      setIsZipLoading(true);
      const address = await AddressService.getAddressByZipCode(digits, userId);
      setValues((prev) => ({
        ...prev,
        zipcode: maskZipCode(digits),
        street: address.street || prev.street,
        neighborhood: address.district || prev.neighborhood,
        city: address.city || prev.city,
        state: (address.state || prev.state || '').toUpperCase(),
      }));
    } catch (_error) {
      lastFetchedZipRef.current = '';
      setZipLookupError('CEP não encontrado. Verifique o CEP informado.');
    } finally {
      setIsZipLoading(false);
    }
  };

  const handleZipCodeChange = (value: string) => {
    const maskedValue = maskZipCode(value || '');
    const digits = maskedValue.replace(/\D/g, '');

    updateField('zipcode', maskedValue);

    if (digits.length < 8) {
      lastFetchedZipRef.current = '';
      return;
    }

    if (digits.length === 8) {
      lookupZipCode(maskedValue);
    }
  };

  const handleSubmit = async () => {
    const validationErrors = validate(values);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length) return;

    try {
      await onSubmit(values);
    } catch (_error) {
      // O erro já é tratado no componente pai para exibição no modal.
    }
  };

  return (
    <Modal open={open} onClose={onClose} size="small" closeIcon>
      <Modal.Header>Cadastrar imóvel</Modal.Header>
      <Modal.Content>
        <p style={{ marginTop: 0 }}>
          Preencha os dados do imóvel para incluir na carteira.
        </p>

        {submitError && <Message negative content={submitError} />}
        {zipLookupError && <Message warning content={zipLookupError} />}

        <Form>
          <Form.Group widths="equal">
            <Form.Field required error={Boolean(errors.zipcode)}>
              <label>CEP</label>
              <Input
                placeholder="00000-000"
                value={values.zipcode}
                loading={isZipLoading}
                onChange={(_, data) => handleZipCodeChange(String(data.value || ''))}
                maxLength={9}
              />
              {errors.zipcode && <small style={{ color: '#9f3a38' }}>{errors.zipcode}</small>}
            </Form.Field>

            <Form.Field required error={Boolean(errors.state)}>
              <label>UF</label>
              <Form.Select
                placeholder="Selecionar..."
                options={ufOptions}
                value={values.state}
                onChange={(_, data) => updateField('state', String(data.value || ''))}
              />
              {errors.state && <small style={{ color: '#9f3a38' }}>{errors.state}</small>}
            </Form.Field>

            <Form.Field required error={Boolean(errors.city)}>
              <label>Cidade</label>
              <Input
                value={values.city}
                onChange={(_, data) => updateField('city', String(data.value || ''))}
              />
              {errors.city && <small style={{ color: '#9f3a38' }}>{errors.city}</small>}
            </Form.Field>
          </Form.Group>

          <Form.Group widths="equal">
            <Form.Field required error={Boolean(errors.street)}>
              <label>Logradouro</label>
              <Input
                value={values.street}
                onChange={(_, data) => updateField('street', String(data.value || ''))}
              />
              {errors.street && <small style={{ color: '#9f3a38' }}>{errors.street}</small>}
            </Form.Field>

            <Form.Field required error={Boolean(errors.number)}>
              <label>Número</label>
              <Input
                value={values.number}
                onChange={(_, data) => updateField('number', String(data.value || ''))}
              />
              {errors.number && <small style={{ color: '#9f3a38' }}>{errors.number}</small>}
            </Form.Field>

            <Form.Field>
              <label>Complemento</label>
              <Input
                value={values.complement}
                onChange={(_, data) => updateField('complement', String(data.value || ''))}
              />
            </Form.Field>
          </Form.Group>

          <Form.Group widths="equal">
            <Form.Field required error={Boolean(errors.neighborhood)}>
              <label>Bairro</label>
              <Input
                value={values.neighborhood}
                onChange={(_, data) => updateField('neighborhood', String(data.value || ''))}
              />
              {errors.neighborhood && <small style={{ color: '#9f3a38' }}>{errors.neighborhood}</small>}
            </Form.Field>

            <Form.Select
              required
              error={Boolean(errors.propertyType)}
              label="Tipo do imóvel"
              placeholder="Selecionar..."
              options={walletPropertyTypeOptions}
              value={values.propertyType}
              onChange={(_, data) => updateField('propertyType', String(data.value || ''))}
            />
            {errors.propertyType && <small style={{ color: '#9f3a38' }}>{errors.propertyType}</small>}

            <Form.Field required={!isLadoBom} error={Boolean(errors.area)}>
              <label>{`Área (m²)${isLadoBom ? ' (opcional)' : ''}`}</label>
              <Input
                placeholder="Ex: 80"
                value={values.area}
                onChange={(_, data) => updateField('area', String(data.value || ''))}
              />
              {errors.area && <small style={{ color: '#9f3a38' }}>{errors.area}</small>}
            </Form.Field>
          </Form.Group>

          <Form.Field required={!isLadoBom} error={Boolean(errors.identification)}>
            <label>{`Identificação${isLadoBom ? ' (opcional)' : ''}`}</label>
            <Input
              placeholder="Ex: APTO 302"
              value={values.identification}
              onChange={(_, data) => updateField('identification', String(data.value || ''))}
            />
            {errors.identification && <small style={{ color: '#9f3a38' }}>{errors.identification}</small>}
          </Form.Field>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button light type="button" onClick={onClose} disabled={isSubmitting}>
          Cancelar
        </Button>
        <Button type="button" onClick={handleSubmit} loading={isSubmitting} disabled={isSubmitting}>
          Cadastrar imóvel
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default CreatePropertyModal;
